import {
  FormControlLabel,
  TextField,
  IconButton,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import Box from "@mui/material/Box";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import dateformat from "dateformat";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { Button, Layout, Paper } from "$components";
import {
  DataTableWithContext,
  EnumBadgeType,
  EnumDataRowType,
  EnumFilterTypes,
  useDataPagination,
} from "$components/DataTable";
import { ImportButtonWithModal } from "$components/Forms/ImportButtonWithModal";
import { Switch } from "$components/Forms/Switch";
import { EMPTY_DATA } from "$containers/constants";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { getEDBs, UpdateEDB } from "$services/EDB.service";
import { ImportType } from "$types/import/ImportType";
import { getEdbStatusOptions } from "$utils/typeConstants/EDB";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { ButtonLink } from "./ButtonLink";
import { ModalCreated } from "./ModalCreated";
import { appState } from "$reducers";
import CollapsedTimeline from "./CollapsedTimeline";
import { CacheInvalidationAction } from "$utils/cache";
import { EdbStatus } from "$types";
import useExport from "$hooks/useExport";

const useMakeCell = (value, type = undefined, badge?, redirect = true) => {
  const { id: companyId } = useParams() as any;
  const link = ({ _id, status }) =>
    status === EdbStatus.CART_CREATED
      ? `/create-edb/${_id}`
      : companyId
      ? `/entreprise/${companyId}/edbs/${_id}`
      : `/edbs/${_id}`;
  const history = useHistory();

  return {
    type,
    link,
    value,
    badge,
    onClick: (row) => {
      if (redirect) history.push(link(row));
    },
  };
};

// Component that can access DataTable context for export
const ExportButton = ({
  isActive,
  keyword,
  isAdmin,
  companySidebarFilter,
  token,
  exportXLS,
}: any) => {
  const [isExporting, setIsExporting] = useState(false);
  const dataTableState = useDataPagination();
  const { filters } = dataTableState;

  const exportAllEdbs = async () => {
    setIsExporting(true);
    try {
      // Use filters directly from DataTable context
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return value !== null && value !== undefined && value !== "";
        })
      );

      const finalFilters = {
        isActive,
        ...(keyword && { keyword }),
        ...cleanedFilters,
        ...(isAdmin() && companySidebarFilter?.name
          ? { company: companySidebarFilter?._id }
          : { exportAll: true }),
      };

      await exportXLS(token, finalFilters, ImportType.EDB_READ, "Liste-EDBs");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button variant="outlined" onClick={exportAllEdbs} disabled={isExporting}>
      {isExporting ? <CircularProgress size={16} /> : <Upload2LineIcon />}
      Exporter toutes les EDB
    </Button>
  );
};

function EDBList() {
  const { isAdmin, isClient, token } = useAuth();
  const [isActive, setisActive] = useState(true);
  const [keyword, setKeyword] = useState<string>("");
  const { exportXLS } = useExport();
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>(
    {}
  );
  const { jclasses } = useJStyles();
  const params = useQueryParameters();
  const { filter: companySidebarFilter } = useSelector(appState);

  const handleSwitch = () => {
    setisActive(!isActive);
  };

  const toggleRowCollapse = (rowId) => {
    setCollapsedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const queryClient = useQueryClient();

  const handleGmaoChange = async (row, newValue) => {
    try {
      await UpdateEDB(token, { ...row, transferGmao: newValue }, row._id);
      queryClient.invalidateQueries([CacheInvalidationAction.GET_EDBS]);
    } catch (error) {
      console.error("Error updating GMAO status:", error);
    }
  };

  const isRowDisabled = (row) => !isActive || !!row?.isDisable;

  const externalState = useMemo(() => {
    const returnState = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of params.entries()) {
      if (!["created", "edbId", "updated"].includes(key)) {
        returnState[key] = value;
      }
    }
    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: {
        ...filters,
        isActive,
        keyword,
        ...(isAdmin() &&
          companySidebarFilter?.name && {
            company: companySidebarFilter?._id,
          }),
      },
      pagination: { page, rowsPerPage },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, keyword, params, companySidebarFilter]);

  const gmaoCell = useMakeCell(
    (row) => (
      <Checkbox
        checked={!!row.transferGmao}
        onChange={(e) => handleGmaoChange(row, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        color="primary"
        disabled={!isActive}
      />
    ),
    null,
    null,
    false
  );

  const headerData = [
    {
      id: "identification",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "identification",
        placeholder: "Par EDB ou entreprise",
        value:
          isAdmin() && companySidebarFilter?.name
            ? companySidebarFilter.name
            : params.get("identification") ?? "",
        disabled: isAdmin() && companySidebarFilter?.name,
      },
      minWidth: 200,
      cell: {
        ...useMakeCell(({ edbId, company }) => {
          return { title: edbId, subtitle: company?.name };
        }, EnumDataRowType.Link),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "intervenantCompany",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Entreprise intervenante",
        placeholder: "Par nom",
        value: params.get("intervenantCompany"),
      },
      minWidth: 120,
      cell: {
        ...useMakeCell(
          ({ intervenantCompany }) => intervenantCompany?.name || "",
          EnumDataRowType.Link
        ),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "createdBy",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Créée par",
        placeholder: "Par utilisateur ou entreprise",
        value: params.get("createdBy"),
      },
      minWidth: 240,
      cell: {
        ...useMakeCell(
          ({ user }) => `${user?.firstname || ""} ${user?.lastname || ""}`,
          EnumDataRowType.Link
        ),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "project",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "projet",
        placeholder: "Par nom ou code",
        value: params.get("project"),
      },
      maxWidth: 200,
      cell: {
        ...useMakeCell(({ project }) => {
          return {
            title: project?.code || EMPTY_DATA,
            subtitle: project?.name || EMPTY_DATA,
          };
        }, EnumDataRowType.Link),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "dockyard",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "chantier",
        placeholder: "Par nom ou code",
        value: params.get("dockyard"),
      },
      maxWidth: 200,
      cell: {
        ...useMakeCell(({ dockyard }) => {
          return {
            title: dockyard?.code || EMPTY_DATA,
            subtitle: dockyard?.name || EMPTY_DATA,
          };
        }, EnumDataRowType.Link),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "nbProducts",
      filter: {
        type: EnumFilterTypes.INPUT_NUMBER,
        label: "produit",
        value: params.get("nbProducts"),
      },
      width: 100,
      cell: {
        ...useMakeCell(({ products, nbIntegratedOrderProducts }) =>
          displayProductsCount(products, nbIntegratedOrderProducts)
        ),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "dates",
      filter: {
        type: EnumFilterTypes.DATE,
        label: "Date de livraison souhaitée",
        value: params.get("dates"),
      },
      width: 180,
      cell: {
        ...useMakeCell(({ dispositionDate }) =>
          dispositionDate
            ? dateformat(new Date(dispositionDate), DEFAULT_DATE_FORMAT)
            : ""
        ),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "status",
      filter: {
        type: EnumFilterTypes.SELECT,
        value: params.getAll("status[]") || params.getAll("status"),
        styleFilter: { maxWidth: 250 },
        options: getEdbStatusOptions(false),
        label: "statut",
        multiple: true,
      },
      Width: 170,
      cell: {
        ...useMakeCell(
          ({ status, dockyard }) => {
            const approver = (dockyard as any)?.approver;
            return {
              status,
              approverName: approver
                ? `${approver.firstname} ${approver.lastname}`
                : undefined,
            };
          },
          EnumDataRowType.Badge,
          EnumBadgeType.Edb
        ),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "approver",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Approbateur",
        placeholder: "Par nom",
        value: params.get("approver"),
      },
      minWidth: 160,
      cell: {
        ...useMakeCell(({ dockyard }) => {
          const approver = (dockyard as any)?.approver;
          return approver
            ? `${approver.firstname} ${approver.lastname.toUpperCase()}`
            : "";
        }, EnumDataRowType.Link),
        isDisabled: isRowDisabled,
      },
    },
    {
      id: "isDisable",
      filter: {
        type: EnumFilterTypes.SELECT,
        label: "Annulé",
        value: params.get("isDisable") ?? "",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non" },
        ],
      },
      width: 110,
      cell: {
        ...useMakeCell((row) => (row.isDisable ? "Oui" : "Non"), null, null),
        isDisabled: isRowDisabled,
      },
    },
    ...(isAdmin()
      ? [
          {
            id: "gmao",
            filter: {
              type: EnumFilterTypes.SELECT,
              label: "Transfert GMAO",
              placeholder: "Par nom",
              value: params.get("gmao"),
              options: [
                { value: "true", label: "Oui" },
                { value: "false", label: "Non" },
              ],
            },
            width: 120,
            cell: {
              ...gmaoCell,
              isDisabled: isRowDisabled,
            },
          },
        ]
      : []),
    {
      id: "timeline",
      label: "Timeline",
      cell: {
        ...useMakeCell(
          (row) => (
            <IconButton
              onClick={() => toggleRowCollapse(row._id)}
              aria-label="Toggle timeline"
            >
              {collapsedRows[row._id] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ),
          null,
          null,
          false
        ),
        isDisabled: isRowDisabled,
      },
    },
  ];

  const displayProductsCount = (products, nbIntegratedOrderProducts) => {
    const nbrProduct = products?.length || 0;
    const totalProductQuantity = (nbIntegratedOrderProducts || 0) + nbrProduct;
    return `${totalProductQuantity} ${
      totalProductQuantity === 1 ? "produit" : "produits"
    }`;
  };

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        contentClassName={jclasses.jLayoutCustom}
        title="Liste des expressions de besoin"
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            filterList={[
              <FormControlLabel
                label="EDB actives"
                control={
                  <Switch
                    onChange={handleSwitch}
                    checked={isActive}
                    color="primary"
                  />
                }
              />,
              <Autocomplete
                options={[]}
                aria-readonly={false as boolean}
                getOptionLabel={(option: any) => option?.name || option}
                onInputChange={(e: any, value: any) => {
                  setKeyword(value);
                }}
                value={keyword}
                freeSolo
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    placeholder="Mot clef..."
                    variant="outlined"
                    size="large"
                    style={{
                      minWidth: 250,
                      minHeight: 37,
                    }}
                  />
                )}
              />,
            ]}
            buttonList={[
              <ButtonLink
                style={{ marginLeft: 8 }}
                to="/create-edb"
                disabled={!isActive}
              >
                Ajouter une EDB
              </ButtonLink>,
              <ExportButton
                isActive={isActive}
                keyword={keyword}
                isAdmin={isAdmin}
                companySidebarFilter={companySidebarFilter}
                token={token}
                exportXLS={exportXLS}
              />,
              (isAdmin() || isClient()) && (
                <ImportButtonWithModal
                  title="Opérations en masse"
                  dataName="EDB"
                  importType={ImportType.EDB}
                  buttonTexts={{
                    importButtonText: "Importer des EDB",
                    updateButtonText: "Mettre à jour des EDB",
                  }}
                  isFeminine
                  disabled={!isActive}
                />
              ),
            ]}
            rowsPerPage={50}
            thead={headerData}
            queryFunction={getEDBs}
            queryFunctionName={CacheInvalidationAction.GET_EDBS}
            CollapsedChildren={CollapsedTimeline}
            data-testid={CacheInvalidationAction.GET_EDBS}
          />

          <ModalCreated />
        </Paper>
      </Layout>
    </Box>
  );
}

export default EDBList;
